import { useRef, useMemo } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import {
  Download,
  ShoppingBag,
  Sparkles,
  CheckCircle2,
  Sun,
  Moon,
  Clock,
  Check,
  ArrowRight,
  Leaf,
  Salad,
  Fish,
  Dumbbell,
  Footprints,
  Heart,
  HeartPulse,
  Stethoscope,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { Button } from "@/components/ui/button";
import axios from "axios";
import {
  PRODUCTS_BUY_URL,
  WHATSAPP_NUMBER,
} from "@/lib/translations";
import { products as CATALOG } from "@/lib/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_BY_AGE = {
  young: "/products/optimizer-performance.png",
  mid: "/products/multi-resveratrol.png",
  mature: "/products/immunocal-platinum.png",
};

function ageBucket(answers = []) {
  const age = answers.find((a) => a.question_id === "age_range")?.answer || "";
  if (age.includes("18") || age.includes("29")) return "young";
  if (age.includes("60")) return "mature";
  if (age.includes("45")) return "mature";
  return "mid";
}

function findCatalogEntry(llmName = "") {
  const n = llmName.toLowerCase();
  return CATALOG.find((p) => {
    const target = p.name.es.toLowerCase();
    const firstWord = target.split(" ")[0];
    return n.includes(target) || n.includes(firstWord) || n.includes(p.slug.split("-")[0]);
  });
}

const HABITS_ES = [
  { icon: Salad, title: "Baja la inflamación", body: "Menos ultraprocesados, azúcar y harinas refinadas." },
  { icon: Fish, title: "Aumenta proteína", body: "En cada comida para apoyar músculos y articulaciones." },
  { icon: Dumbbell, title: "Fuerza ligera", body: "Fortalece piernas, espalda y core 2-3 veces por semana." },
  { icon: Footprints, title: "Camina diario", body: "Mejora movilidad, circulación y control de peso." },
  { icon: Heart, title: "Colágeno + Vit. C", body: "Apoyo extra para cartílago y tejidos." },
  { icon: HeartPulse, title: "Monitorea bienestar", body: "Lleva registro de energía, sueño y presión arterial." },
];

export default function ProtocolResult({ t, language, profile, protocol, answers, questions }) {
  const infoRef = useRef(null);

  const month1 = protocol.months?.[0];
  const ageAnswer = (answers && questions)
    ? questions.map((q) => ({ question_id: q.id, answer: answers[q.id] || "" }))
    : [];
  const ageKey = ageBucket(ageAnswer);
  const heroSrc = HERO_BY_AGE[ageKey];

  // Get up to 4 unique products from month 1
  const featured = useMemo(() => {
    const seen = new Set();
    const list = [];
    (month1?.products || []).forEach((p) => {
      if (seen.has(p.name)) return;
      const cat = findCatalogEntry(p.name);
      seen.add(p.name);
      list.push({ ...p, catalog: cat });
    });
    // Top up from month 2/3 if needed
    if (list.length < 4) {
      (protocol.months || []).slice(1).forEach((m) =>
        (m.products || []).forEach((p) => {
          if (list.length >= 4 || seen.has(p.name)) return;
          seen.add(p.name);
          list.push({ ...p, catalog: findCatalogEntry(p.name) });
        })
      );
    }
    return list.slice(0, 4);
  }, [protocol]);

  const allMes1 = month1?.products || [];
  const allMes23 = (protocol.months || []).slice(1).flatMap((m) => m.products || []);

  // --- WhatsApp self-send (auto-open once) ---
  const customerWhatsAppLink = useMemo(() => {
    const phone = (profile.phone || "").replace(/\D/g, "");
    if (!phone) return null;
    const greeting = `*Tu Protocolo Eliora Longevity, ${profile.name.split(" ")[0]}* 🌿\n\n${protocol.summary}\n\n`;
    const months = (protocol.months || [])
      .map((m) => {
        const ps = (m.products || []).map((p) => `• ${p.name} — ${p.dosage}`).join("\n");
        return `*Mes ${m.month}: ${m.title}*\n${ps}`;
      })
      .join("\n\n");
    const buy = `\n\n${language === "es" ? "Adquiérelos" : "Buy them"}: ${PRODUCTS_BUY_URL}`;
    return `https://wa.me/${phone}?text=${encodeURIComponent(greeting + months + buy)}`;
  }, [profile, protocol, language]);

  // --- WhatsApp link to schedule appointment with certified Immunotec physician ---
  const appointmentWhatsAppLink = useMemo(() => {
    const firstName = profile.name.split(" ")[0];
    const text =
      language === "es"
        ? `Hola, soy ${firstName}. Acabo de completar el Wellness Quiz de Eliora Longevity y me gustaría agendar una cita con un Médico Cirujano Certificado de Immunotec. ¡Gracias!`
        : `Hi, I'm ${firstName}. I just completed the Eliora Longevity Wellness Quiz and would like to schedule an appointment with an Immunotec Certified Physician. Thanks!`;
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;
  }, [profile, language]);

  // NOTE: WhatsApp is NOT auto-opened anymore. The user must explicitly
  // click "Recibir mi protocolo en WhatsApp" below to open the chat.

  const capture = async () => {
    if (!infoRef.current) return null;
    return html2canvas(infoRef.current, {
      backgroundColor: "#f9f6f0",
      scale: 2,
      useCORS: true,
      logging: false,
    });
  };

  const downloadJpg = async () => {
    const canvas = await capture();
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/jpeg", 0.92);
    a.download = `protocolo-eliora-${profile.name.split(" ")[0]}.jpg`;
    a.click();
  };

  const downloadPdf = async () => {
    const canvas = await capture();
    if (!canvas) return;
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const imgHeight = (canvas.height * pageWidth) / canvas.width;

    if (imgHeight <= pageHeight) {
      pdf.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", 0, 0, pageWidth, imgHeight);
      pdf.save(`protocolo-eliora-${profile.name.split(" ")[0]}.pdf`);
      return;
    }

    // ---------- Smart page-break: never cut inside a block ----------
    // Build a sorted list of "safe break points" = the BOTTOM Y of every
    // top-level section AND of nested cards/items inside grids. We then
    // slice each page at the deepest break point that still fits.
    const root = infoRef.current;
    const rootRect = root.getBoundingClientRect();
    const scaleY = canvas.height / rootRect.height; // DOM px → canvas px
    const pageHeightPx = (pageHeight * canvas.width) / pageWidth; // canvas px per A4 page

    const breakPoints = new Set([0, canvas.height]);
    const collectBreaks = (el, depth = 0) => {
      if (depth > 5) return;
      Array.from(el.children).forEach((child) => {
        const r = child.getBoundingClientRect();
        const topPx = (r.top - rootRect.top) * scaleY;
        const bottomPx = (r.bottom - rootRect.top) * scaleY;
        // Only collect items taller than ~8px (skip empty wrappers).
        // For LI items, force their bottom edge as a break point.
        if (bottomPx - topPx > 8) {
          breakPoints.add(Math.round(topPx));
          breakPoints.add(Math.round(bottomPx));
        }
        collectBreaks(child, depth + 1);
      });
    };
    collectBreaks(root);
    const sortedBreaks = Array.from(breakPoints).sort((a, b) => a - b);

    const findNextBreak = (yStart) => {
      const maxY = yStart + pageHeightPx;
      // Largest break point ≤ maxY and > yStart
      let best = yStart;
      for (const bp of sortedBreaks) {
        if (bp > yStart && bp <= maxY) best = bp;
        if (bp > maxY) break;
      }
      // Safety net: if no break point found (single block taller than page),
      // force a hard cut at exactly page height.
      if (best === yStart) best = Math.min(yStart + pageHeightPx, canvas.height);
      return best;
    };

    let yPx = 0;
    let pageIndex = 0;
    while (yPx < canvas.height - 1) {
      const yEnd = findNextBreak(yPx);
      const sliceHeightPx = yEnd - yPx;
      const sliceCanvas = document.createElement("canvas");
      sliceCanvas.width = canvas.width;
      sliceCanvas.height = sliceHeightPx;
      const ctx = sliceCanvas.getContext("2d");
      ctx.fillStyle = "#f9f6f0";
      ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
      ctx.drawImage(canvas, 0, yPx, canvas.width, sliceHeightPx, 0, 0, canvas.width, sliceHeightPx);
      const sliceData = sliceCanvas.toDataURL("image/jpeg", 0.92);
      const sliceHeightMm = (sliceHeightPx * pageWidth) / canvas.width;
      if (pageIndex > 0) pdf.addPage();
      pdf.addImage(sliceData, "JPEG", 0, 0, pageWidth, sliceHeightMm);
      yPx = yEnd;
      pageIndex += 1;
    }

    pdf.save(`protocolo-eliora-${profile.name.split(" ")[0]}.pdf`);
  };

  // Build "main concerns" string for header
  const concerns = (protocol.key_concerns || []).slice(0, 3).join(" · ");

  return (
    <div className="bg-[#f9f6f0]">
      {/* Auto-sent banner */}
      <div className="bg-[#2c4a3b]/95 text-[#f9f6f0] px-8 md:px-10 py-4 flex items-start gap-3" data-testid="auto-sent-banner">
        <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0 text-[#bc6c25]" />
        <p className="text-sm leading-relaxed">{t.protocol.sentBanner}</p>
      </div>

      {/* ===== INFOGRAPHIC ===== */}
      <div ref={infoRef} className="bg-[#f9f6f0] p-6 md:p-10 space-y-8">
        {/* HEADER */}
        <div className="grid grid-cols-12 gap-6 items-center">
          <div className="col-span-7">
            <div className="flex items-center gap-2 text-[#bc6c25]">
              <div className="w-7 h-7 rounded-full border border-[#bc6c25] grid place-items-center font-serif italic text-sm">
                E
              </div>
              <span className="text-overline">Eliora Longevity</span>
            </div>
            <h1
              className="font-serif font-light leading-[0.95] text-4xl md:text-6xl mt-3 text-[#1a2421]"
              data-testid="protocol-headline"
            >
              PROTOCOLO<br />
              <span className="italic text-[#2c4a3b]">{profile.name.split(" ")[0]}</span>
            </h1>
            <p className="mt-4 text-[11px] font-bold uppercase tracking-[0.18em] text-[#5c6b61]">
              {concerns}
            </p>
            <p className="mt-3 text-sm text-[#5c6b61] leading-relaxed max-w-md">
              {protocol.summary}
            </p>
          </div>
          <div className="col-span-3">
            <img src={heroSrc} alt="" className="w-full aspect-square object-cover rounded-2xl" />
          </div>
          <div className="col-span-2 space-y-4">
            {[
              { icon: Leaf, t: "Ciencia que nutre" },
              { icon: Sparkles, t: "Bienestar que se siente" },
              { icon: Heart, t: "Resultados que transforman" },
            ].map(({ icon: Icon, t: txt }, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 rounded-full border border-[#2c4a3b]/20 grid place-items-center mx-auto">
                  <Icon className="w-5 h-5 text-[#2c4a3b]" />
                </div>
                <p className="text-[9px] font-bold uppercase tracking-[0.14em] text-[#5c6b61] mt-2 leading-tight">
                  {txt}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* 4 PRODUCT CARDS */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {featured.map((p, i) => (
            <a
              key={i}
              href={PRODUCTS_BUY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`featured-product-${i}`}
              className="bg-white rounded-2xl p-5 border border-[#2c4a3b]/10 flex flex-col hover:border-[#bc6c25] hover:shadow-lg transition-all group"
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="w-7 h-7 rounded-full border-2 border-[#2c4a3b] text-[#2c4a3b] grid place-items-center text-xs font-bold">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#1a2421]">
                  {(p.catalog?.name.es || p.name).toUpperCase()}
                </p>
              </div>
              <p className="text-xs text-[#5c6b61] leading-snug mb-3 flex-1">
                {p.catalog?.tagline.es || p.reason}
              </p>
              {p.catalog?.officialImage ? (
                <div className="aspect-square bg-[#f0ebe1] rounded-xl flex items-center justify-center p-3 mb-3">
                  <img src={p.catalog.officialImage} alt={p.name} className="max-w-full max-h-full object-contain" />
                </div>
              ) : (
                <div className="aspect-square bg-[#f0ebe1] rounded-xl mb-3" />
              )}
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#2c4a3b] mb-2">
                Cómo tomarlo
              </p>
              <p className="text-[11px] text-[#1a2421] leading-relaxed flex items-start gap-1.5">
                <Sun className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#bc6c25]" />
                {p.dosage}
              </p>
            </a>
          ))}
        </div>

        {/* PROTOCOLO SUGERIDO Timeline */}
        <div className="text-center">
          <p className="text-overline">Protocolo sugerido</p>
        </div>
        <div className="grid grid-cols-11 gap-4 items-stretch">
          <div className="col-span-5 bg-white rounded-2xl p-6 border border-[#2c4a3b]/10">
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#2c4a3b] mb-3">Mes 1</p>
            <ul className="space-y-3">
              {allMes1.map((p, i) => (
                <li key={i} className="text-sm">
                  <a
                    href={PRODUCTS_BUY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`mes1-link-${i}`}
                    className="group block hover:bg-[#bc6c25]/5 -m-1 p-1 rounded transition-colors"
                  >
                    <p className="font-semibold text-[#1a2421] underline decoration-[#bc6c25]/40 decoration-1 underline-offset-4 group-hover:decoration-[#bc6c25]">
                      {p.name}
                    </p>
                    <p className="text-xs text-[#5c6b61] mt-0.5">{p.dosage}</p>
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div className="col-span-1 flex flex-col items-center justify-center text-center">
            <div className="w-12 h-12 rounded-full border border-[#2c4a3b]/30 grid place-items-center bg-white">
              <ArrowRight className="w-5 h-5 text-[#2c4a3b]" />
            </div>
            <p className="text-[9px] uppercase tracking-[0.14em] text-[#5c6b61] mt-2 leading-tight">
              Evaluar cómo te sientes
            </p>
          </div>
          <div className="col-span-5 bg-white rounded-2xl p-6 border border-[#bc6c25]/40">
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#bc6c25] mb-3">Meses 2 – 3</p>
            <ul className="space-y-3">
              {allMes23.slice(0, 5).map((p, i) => (
                <li key={i} className="text-sm">
                  <a
                    href={PRODUCTS_BUY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`mes23-link-${i}`}
                    className="group block hover:bg-[#bc6c25]/5 -m-1 p-1 rounded transition-colors"
                  >
                    <p className="font-semibold text-[#1a2421] underline decoration-[#bc6c25]/40 decoration-1 underline-offset-4 group-hover:decoration-[#bc6c25]">
                      {p.name}
                    </p>
                    <p className="text-xs text-[#5c6b61] mt-0.5">{p.dosage}</p>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* BENEFICIOS ESPERADOS */}
        {protocol.key_concerns?.length > 0 && (
          <div className="bg-[#f0ebe1]/60 rounded-2xl p-6">
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#2c4a3b] mb-4">
              Beneficios esperados
            </p>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
              {(protocol.months?.[0]?.lifestyle || [])
                .concat(protocol.months?.[0]?.daily_routine || [])
                .slice(0, 6)
                .map((b, i) => (
                  <li key={i} className="text-sm text-[#1a2421] flex items-start gap-2">
                    <Check className="w-4 h-4 mt-0.5 shrink-0 text-[#2c4a3b]" />
                    <span className="leading-snug">{b}</span>
                  </li>
                ))}
            </ul>
          </div>
        )}

        {/* HÁBITOS SALUDABLES */}
        <div>
          <p className="text-center text-[10px] font-bold uppercase tracking-[0.22em] text-[#2c4a3b] mb-5">
            Combina suplementos con hábitos saludables
          </p>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
            {HABITS_ES.map((h, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 rounded-full bg-[#bc6c25]/10 grid place-items-center mx-auto">
                  <h.icon className="w-5 h-5 text-[#bc6c25]" />
                </div>
                <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#1a2421] mt-2 leading-tight">
                  {h.title}
                </p>
                <p className="text-[10px] text-[#5c6b61] mt-1 leading-snug">
                  {h.body}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* CLOSING NOTE */}
        {protocol.closing_note && (
          <div className="bg-[#2c4a3b] text-[#f9f6f0] rounded-2xl p-6">
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#bc6c25] mb-2">
              Una nota de tu asesora
            </p>
            <p className="font-serif italic text-lg leading-relaxed">{protocol.closing_note}</p>
          </div>
        )}

        {/* IMPORTANTE */}
        <div className="border-t border-[#2c4a3b]/15 pt-5 text-[10px] text-[#5c6b61]/80 leading-relaxed">
          <p className="font-bold uppercase tracking-[0.18em] text-[#bc6c25] mb-2">Importante</p>
          {t.protocol.disclaimer} Consulta siempre a tu médico antes de comenzar cualquier suplemento, especialmente si tomas medicamentos.
        </div>

        <div className="text-center text-[10px] text-[#5c6b61]/70 tracking-[0.18em] uppercase">
          elioralongevity.com · Asociado Independiente Immunotec
        </div>
      </div>

      {/* ===== ACTION TOOLBAR ===== */}
      <div className="px-8 md:px-10 pb-10 pt-6 border-t border-[#2c4a3b]/10 bg-[#f0ebe1]/60">
        {/* Pricing options */}
        <div className="bg-white rounded-2xl p-6 border border-[#2c4a3b]/10" data-testid="pricing-options">
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#2c4a3b]">
            {t.protocol.pricingTitle}
          </p>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            <div className="rounded-xl border border-[#2c4a3b]/15 p-5">
              <p className="font-serif text-lg text-[#1a2421] leading-snug">{t.protocol.pricingOption1Label}</p>
              <p className="mt-2 text-xs text-[#5c6b61] leading-relaxed">{t.protocol.pricingOption1Note}</p>
            </div>
            <div className="rounded-xl border-2 border-[#bc6c25] bg-[#bc6c25]/5 p-5 relative">
              <span className="absolute -top-2 right-4 bg-[#bc6c25] text-white text-[10px] font-bold uppercase tracking-[0.18em] rounded-full px-2.5 py-0.5">
                {language === "es" ? "Recomendado" : "Recommended"}
              </span>
              <p className="font-serif text-lg text-[#1a2421] leading-snug">{t.protocol.pricingOption2Label}</p>
              <p className="mt-2 text-xs text-[#5c6b61] leading-relaxed">{t.protocol.pricingOption2Note}</p>
            </div>
          </div>
        </div>

        <a
          href={PRODUCTS_BUY_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="protocol-buy-btn"
          className="mt-5 group inline-flex items-center justify-center gap-3 w-full rounded-full bg-[#bc6c25] hover:bg-[#9c581e] text-white px-6 py-5 text-base font-semibold tracking-wide transition-all lift"
        >
          <ShoppingBag className="w-5 h-5" />
          {t.protocol.buyProducts}
          <span className="transition-transform group-hover:translate-x-1">→</span>
        </a>

        {customerWhatsAppLink && (
          <a
            href={customerWhatsAppLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="customer-whatsapp-btn"
            className="mt-3 group inline-flex items-center justify-center gap-3 w-full rounded-full bg-[#25D366] hover:bg-[#1eb854] text-white px-6 py-4 text-sm font-semibold tracking-wide transition-all"
          >
            <FaWhatsapp className="w-5 h-5" />
            {language === "es" ? "Recibir mi protocolo en WhatsApp" : "Receive my protocol on WhatsApp"}
          </a>
        )}

        <a
          href={appointmentWhatsAppLink}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="schedule-appointment-btn"
          className="mt-3 group inline-flex items-center justify-center gap-3 w-full rounded-full border-2 border-[#2c4a3b] text-[#2c4a3b] hover:bg-[#2c4a3b] hover:text-white px-6 py-4 text-sm font-semibold tracking-wide transition-all"
        >
          <Stethoscope className="w-5 h-5" />
          {language === "es"
            ? "Agendar cita con Médico Cirujano Certificado de Immunotec"
            : "Schedule an appointment with a Certified Immunotec Physician"}
        </a>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <Button onClick={downloadJpg} data-testid="download-jpg-btn" variant="outline" className="rounded-full border-[#2c4a3b]/30 text-[#1a2421] hover:bg-[#2c4a3b]/5">
            <Download className="w-4 h-4 mr-2" />
            {t.protocol.downloadJpg}
          </Button>
          <Button onClick={downloadPdf} data-testid="download-pdf-btn" variant="outline" className="rounded-full border-[#2c4a3b]/30 text-[#1a2421] hover:bg-[#2c4a3b]/5">
            <Download className="w-4 h-4 mr-2" />
            {t.protocol.downloadPdf}
          </Button>
        </div>
      </div>
    </div>
  );
}
